/**
 * Product API Transforms
 *
 * Maps between the API response shapes (ApiProductListItem / ApiProductDetail)
 * and the view-model shape (Product) used by the UI components.
 *
 * Also provides helpers for building API request bodies from the
 * multi-step registration form payload.
 */
import type {
  ApiProductListItem,
  ApiProductDetail,
  ApiProductStatus,
  CreateProductApiRequest,
  UpdateProductApiRequest,
} from '../types/product-api.types';
import type { Product, ProductStatus } from '../types';
import type { ProductRegistrationPayload } from '../types/product-form';

// ---------------------------------------------------------------------------
// Status mapping helpers
// ---------------------------------------------------------------------------

/**
 * Map an API product status to the view-model status used by UI components.
 *
 *  API status  → View status
 *  active      → active
 *  draft       → pending
 *  deprecated  → inactive
 *  archived    → inactive
 */
export function apiStatusToViewStatus(status: ApiProductStatus | string): ProductStatus {
  switch (status) {
    case 'active':
      return 'active';
    case 'draft':
      return 'pending';
    case 'deprecated':
    case 'archived':
    default:
      return 'inactive';
  }
}

/**
 * Map a view-model ProductStatus to the closest ApiProductStatus for writes.
 *
 *  View status → API status
 *  active      → active
 *  inactive    → deprecated
 *  pending     → draft
 */
export function viewStatusToApiStatus(status: ProductStatus): ApiProductStatus {
  switch (status) {
    case 'active':
      return 'active';
    case 'inactive':
      return 'deprecated';
    case 'pending':
    default:
      return 'draft';
  }
}

/** Set of valid API product statuses for runtime validation. */
const VALID_API_STATUSES: ReadonlySet<string> = new Set<ApiProductStatus>([
  'active',
  'draft',
  'deprecated',
  'archived',
]);

/**
 * Validate a freeform string against the allowed ApiProductStatus values.
 * Returns the matching ApiProductStatus (case-insensitive) or undefined when
 * the input is empty/blank. Throws a descriptive Error when the input is
 * non-empty but does not match any allowed status, so the UI can surface
 * the problem rather than silently dropping the value.
 */
export function toApiStatus(raw: string | undefined | null): ApiProductStatus | undefined {
  if (!raw || !raw.trim()) return undefined;
  const normalised = raw.trim().toLowerCase();
  if (VALID_API_STATUSES.has(normalised)) return normalised as ApiProductStatus;
  const allowed = Array.from(VALID_API_STATUSES).join(', ');
  throw new Error(`Invalid product status "${raw.trim()}". Allowed values: ${allowed}.`);
}

// ---------------------------------------------------------------------------
// API → View-model transforms
// ---------------------------------------------------------------------------

/**
 * Transform an ApiProductListItem to the Product view-model.
 *
 * Field mapping:
 *   product_id        → id
 *   product_name      → productName
 *   description       → description  (free-text product description from API)
 *   brand_name        → brand
 *   form_name         → form
 *   image_url         → imageUrl
 *   manufacturer_name → manufacturer
 *   category_name     → category
 *   ndc_format_code   → ndc
 *   created_at        → createdAt
 *   updated_at        → updatedAt
 */
export function apiProductToViewModel(item: ApiProductListItem | ApiProductDetail): Product {
  return {
    id: item.product_id,
    sku: item.sku,
    productName: item.product_name,
    description: item.description ?? '',
    brand: item.brand_name ?? '',
    form: item.form_name ?? '',
    status: apiStatusToViewStatus(item.status),
    imageUrl: item.image_url ?? undefined,
    manufacturer: item.manufacturer_name ?? undefined,
    category: Array.isArray(item.category_name) ? item.category_name.join(', ') : (item.category_name ?? undefined),
    ndc: item.ndc_format_code ?? undefined,
    createdAt: item.created_at,
    updatedAt: item.updated_at,
    // Always convert to the nested ProductRegistrationPayload structure so the
    // ProductViewDialog can read data via regData.overview.*, regData.nutritions.*, etc.
    // apiDetailToFormPayload handles undefined fields gracefully (returns '' for missing values).
    registrationData: apiDetailToFormPayload(item as ApiProductDetail) as unknown as Record<string, unknown>,
  };
}

// ---------------------------------------------------------------------------
// Form payload → API request bodies
// ---------------------------------------------------------------------------

/**
 * Parse a numeric string field from form data.
 * Returns undefined when the string is empty/undefined, number otherwise.
 */
function parseNumeric(val: string | undefined | null): number | undefined {
  if (!val || val.trim() === '') return undefined;
  const n = Number(val);
  return Number.isNaN(n) ? undefined : n;
}

/**
 * Parse a boolean-ish string field used by dietary information fields.
 * Recognises "Yes" / "No" / "true" / "false". An empty/cleared value returns
 * `null` (clear this field on PATCH) rather than `undefined` (leave unchanged) —
 * otherwise deselecting the dropdown could never persist.
 */
function parseBoolString(val: string | undefined | null): boolean | null | undefined {
  if (!val) return null;
  const lower = val.toLowerCase().trim();
  if (lower === 'yes' || lower === 'true') return true;
  if (lower === 'no' || lower === 'false') return false;
  return undefined;
}

// ---------------------------------------------------------------------------
// Allergens: flat form fields → boolean flags for the flat API body
// ---------------------------------------------------------------------------

/** Mapping from form allergen field key to the flat API boolean field name. */
const ALLERGEN_KEY_TO_API_FIELD: Record<string, keyof CreateProductApiRequest> = {
  egg: 'contains_allergen_egg',
  fish: 'contains_allergen_fish',
  lactose: 'contains_allergen_lactose',
  latex: 'contains_allergen_latex',
  milk: 'contains_allergen_milk',
  peanut: 'contains_allergen_peanut',
  sesame: 'contains_allergen_sesame',
  shellfish: 'contains_allergen_shellfish',
  soy: 'contains_allergen_soy',
  tree_nuts: 'contains_allergen_tree_nuts',
  wheat: 'contains_allergen_wheat',
  contains_allergen_crustaceans: 'contains_allergen_shellfish', // crustaceans → shellfish
  contains_allergen_molluscs: 'contains_allergen_molluscs',
  contains_allergen_celery: 'contains_allergen_celery',
  contains_allergen_mustard: 'contains_allergen_mustard',
  contains_allergen_sulphur_dioxide: 'contains_allergen_sulphites', // sulphur dioxide → sulphites
  contains_allergen_sulphites: 'contains_allergen_sulphites',
  contains_allergen_lupin: 'contains_allergen_lupin',
};

/**
 * Map the boolean allergens form fields directly to API boolean flags.
 */
function buildAllergenFlags(
  allergens: Record<string, boolean | undefined>,
): Partial<CreateProductApiRequest> {
  const flags: Partial<CreateProductApiRequest> = {};
  for (const [key, value] of Object.entries(allergens)) {
    if (value === undefined) continue;
    const apiField = ALLERGEN_KEY_TO_API_FIELD[key];
    if (!apiField) continue;
    (flags as Record<string, boolean>)[apiField] = !!value;
  }
  return flags;
}

// ---------------------------------------------------------------------------
// Form payload → CreateProductApiRequest (POST — flat body)
// ---------------------------------------------------------------------------

/**
 * Build a flat CreateProductApiRequest from the multi-step form payload.
 * All nutritional, allergen, and packaging fields are at the top level.
 */
export function buildCreateProductRequest(
  payload: ProductRegistrationPayload,
): CreateProductApiRequest {
  const bi = payload.overview.basic_information;
  const desc = payload.overview.description;
  const exp = payload.overview.expiration;
  const other = payload.other;
  const { nutritions, vitamins, minerals, amino_acids, packaging } = payload;
  const ci = nutritions.caloric_information;
  const prot = nutritions.protein;
  const fat = nutritions.fat;
  const carbs = nutritions.carbohydrates;
  const diet = nutritions.dietary_information;
  const serve = nutritions.serving_information;
  const ingr = nutritions.ingredients;

  const status: ApiProductStatus = bi.product_status?.toLowerCase() === 'active' ? 'active' : 'draft';
  const allergenFlags = buildAllergenFlags(
    payload.overview.allergens as unknown as Record<string, boolean | undefined>,
  );

  return {
    sku: bi.sku,
    product_name: bi.brand_name || bi.sku,
    status,
    is_active: true, // always true on create; will be handled in a future iteration
    description: desc.product_description || undefined,
    // null (not undefined) so clearing the dropdown persists on PATCH — see joinCsv/parseBoolString above.
    type_of_product: bi.type_of_product || null,
    brand_name: bi.brand_name || undefined,
    form_name: bi.form || undefined,
    category_name: desc.formula_classification?.length ? desc.formula_classification : [],
    parenteral_category: desc.parenteral_category || undefined,
    manufacturer_name: bi.manufacturer || undefined,
    tms_product_id: bi.tms_product_id || undefined,
    hcpcs: bi.hcpcs || undefined,
    ndc_format_code: bi.ndc_format_code || undefined,
    substitutions: desc.substitutions || undefined,
    date_verified: bi.date_verified || undefined,
    verification_source: payload.overview.verification?.verification_source_url || undefined,
    overarching_number_pens: other.identifiers?.over_arching_number_pens || undefined,
    overarching_number_ns: other.identifiers?.over_arching_number_nutrition_source || undefined,
    overarching_number_tmnp: other.identifiers?.over_arching_number_tmnp || undefined,
    similar_products: other.review_similar_products.similar_products
      ? other.review_similar_products.similar_products.split(',').map((s) => s.trim()).filter(Boolean)
      : undefined,

    // -- Allergen boolean flags -----------------------------------------------
    ...allergenFlags,

    // -- Energy / Calories ----------------------------------------------------
    calories_kcal: parseNumeric(bi.calories_for_pwds) ?? parseNumeric(bi.calories_for_liquids),
    kcal_per_ml: parseNumeric(bi.calories_for_liquids),
    kcal_per_g: parseNumeric(bi.calories_for_pwds),

    // -- Physical / osmotic ---------------------------------------------------
    osmolality_mosm_per_kg: parseNumeric(ci.osmolality),
    osmolarity_mosm_per_l: parseNumeric(ci.osmolarity),
    water_g: parseNumeric(ci.water),
    prsl_mosm_per_100kcal: parseNumeric(ci.prsl),
    renal_solute_load_mosml: parseNumeric(ci.renal_solute_load),
    displacement_ml_per_g: parseNumeric(ci.displacement_for_pwd),
    ml_per_100kcal: parseNumeric(ci.volume_to_provide_100_kcal),

    // -- Protein --------------------------------------------------------------
    protein_g: parseNumeric(prot.protein),
    protein_source: joinCsv(prot.protein_source),
    protein_hydrolysis: joinCsv(prot.protein_hydrolysis),
    total_nitrogen_g: parseNumeric(prot.total_n2),

    // -- Fat ------------------------------------------------------------------
    total_fat_g: parseNumeric(fat.fat),
    mct_g: parseNumeric(fat.mct),
    ala_g: parseNumeric(fat.ala),
    linoleic_omega6_mg: parseNumeric(fat.linoleic_acid_omega_6),
    linolenic_omega3_mg: parseNumeric(fat.linolenic_acid_omega_3),
    ara_mg: parseNumeric(fat.ara),
    dha_mg: parseNumeric(fat.dha),
    epa_mg: parseNumeric(fat.epa),
    cholesterol_mg: parseNumeric(fat.cholesterol),

    // -- Carbohydrates --------------------------------------------------------
    cho_g: parseNumeric(carbs.cho),
    cho_source: joinCsv(carbs.cho_source),
    total_carbohydrate_g: parseNumeric(carbs.cho),
    added_sugars_g: parseNumeric(carbs.added_sugars),
    total_sugars_g: parseNumeric(carbs.total_sugars),
    dietary_fiber_g: parseNumeric(carbs.dietary_fiber),
    soluble_fiber_g: parseNumeric(carbs.soluble_fiber),
    insoluble_fiber_g: parseNumeric(carbs.insoluble_fiber),
    prebiotic_fiber_g: parseNumeric(carbs.prebiotic_fiber),
    dextrose_g: parseNumeric(carbs.dextrose),
    d_galactose_g: parseNumeric(carbs.d_galactose),

    // -- Dietary flags --------------------------------------------------------
    gluten_free: parseBoolString(diet.gluten_free),
    halal: parseBoolString(diet.halal),
    kosher: parseBoolString(diet.kosher),

    // -- Serving information --------------------------------------------------
    scoop_g: parseNumeric(serve.one_unpacked_level_scoop),
    teaspoon_g: parseNumeric(serve.one_level_unpacked_teaspoon),
    cup_g: parseNumeric(serve.one_level_unpacked_cup),
    nutrient_basis: serve.denominator_property || undefined,
    recipe_dilution: serve.recipe_dilution_for_reconstitution || undefined,

    // -- Ingredients ----------------------------------------------------------
    ingredients: ingr.full_ingredients_list || undefined,

    // -- Vitamins - B vitamins ------------------------------------------------
    biotin_mcg: parseNumeric(vitamins.b_vitamins.biotin),
    folate_mcg: parseNumeric(vitamins.b_vitamins.folic_acid),
    folate_dfe_mcg: parseNumeric(vitamins.b_vitamins.folate),
    niacin_b3_mg: parseNumeric(vitamins.b_vitamins.niacin),
    pantothenic_acid_mg: parseNumeric(vitamins.b_vitamins.pantothenic_acid),
    riboflavin_b2_mg: parseNumeric(vitamins.b_vitamins.riboflavin),
    thiamin_b1_mg: parseNumeric(vitamins.b_vitamins.thiamin),
    vitamin_b12_mcg: parseNumeric(vitamins.b_vitamins.vitamin_b12),
    vitamin_b6_mg: parseNumeric(vitamins.b_vitamins.vitamin_b6),

    // -- Vitamins - Fat soluble -----------------------------------------------
    vitamin_a_mcg: parseNumeric(vitamins.fat_soluble_vitamins.vitamin_a_rae),
    vitamin_d_mcg: parseNumeric(vitamins.fat_soluble_vitamins.vitamin_d_mcg),
    vitamin_e_mg: parseNumeric(vitamins.fat_soluble_vitamins.vitamin_e_mg),
    vitamin_k_mcg: parseNumeric(vitamins.fat_soluble_vitamins.vitamin_k),

    // -- Vitamins - Other -----------------------------------------------------
    choline_mg: parseNumeric(vitamins.other_vitamins_related.choline),
    inositol_mg: parseNumeric(vitamins.other_vitamins_related.inositol),
    vitamin_c_mg: parseNumeric(vitamins.other_vitamins_related.vitamin_c),

    // -- Minerals - Electrolytes ----------------------------------------------
    sodium_mg: parseNumeric(minerals.electrolytes.sodium_mg),
    sodium_meq: parseNumeric(minerals.electrolytes.sodium_meq),
    potassium_mg: parseNumeric(minerals.electrolytes.potassium_mg),
    potassium_meq: parseNumeric(minerals.electrolytes.potassium_meq),
    chloride_mg: parseNumeric(minerals.electrolytes.chloride_mg),
    acetate_meq: parseNumeric(minerals.electrolytes.acetate),

    // -- Minerals - Major -----------------------------------------------------
    calcium_mg: parseNumeric(minerals.major_minerals.calcium_mg),
    calcium_meq: parseNumeric(minerals.major_minerals.calcium_meq),
    magnesium_mg: parseNumeric(minerals.major_minerals.magnesium_mg),
    magnesium_meq: parseNumeric(minerals.major_minerals.magnesium_meq),
    phosphorus_mg: parseNumeric(minerals.major_minerals.phosphorus_mg),
    phosphorus_mmol: parseNumeric(minerals.major_minerals.phosphorus_mmol),

    // -- Minerals - Trace -----------------------------------------------------
    iron_mg: parseNumeric(minerals.trace_minerals.iron),
    zinc_mg: parseNumeric(minerals.trace_minerals.zinc),
    copper_mg: parseNumeric(minerals.trace_minerals.copper),
    manganese_mg: parseNumeric(minerals.trace_minerals.manganese),
    selenium_mcg: parseNumeric(minerals.trace_minerals.selenium),
    chromium_mcg: parseNumeric(minerals.trace_minerals.chromium),
    molybdenum_mcg: parseNumeric(minerals.trace_minerals.molybdenum),
    iodine_mcg: parseNumeric(minerals.trace_minerals.iodine),
    fluoride_mcg: parseNumeric(minerals.trace_minerals.fluoride),
    aluminum_mcg: parseNumeric(minerals.trace_minerals.aluminum),

    // -- Essential amino acids ------------------------------------------------
    histidine_g: parseNumeric(amino_acids.essential_amino_acids.histidine),
    isoleucine_g: parseNumeric(amino_acids.essential_amino_acids.isoleucine),
    leucine_g: parseNumeric(amino_acids.essential_amino_acids.leucine),
    lysine_g: parseNumeric(amino_acids.essential_amino_acids.lysine),
    methionine_g: parseNumeric(amino_acids.essential_amino_acids.methionine),
    phenylalanine_g: parseNumeric(amino_acids.essential_amino_acids.phenylalanine),
    threonine_g: parseNumeric(amino_acids.essential_amino_acids.threonine),
    tryptophan_g: parseNumeric(amino_acids.essential_amino_acids.tryptophan),
    valine_g: parseNumeric(amino_acids.essential_amino_acids.valine),

    // -- Non-essential amino acids --------------------------------------------
    alanine_g: parseNumeric(amino_acids.non_essential_amino_acids.alanine),
    arginine_g: parseNumeric(amino_acids.non_essential_amino_acids.arginine),
    cystine_g: parseNumeric(amino_acids.non_essential_amino_acids.cystine),
    glycine_g: parseNumeric(amino_acids.non_essential_amino_acids.glycine),
    proline_g: parseNumeric(amino_acids.non_essential_amino_acids.proline),
    serine_g: parseNumeric(amino_acids.non_essential_amino_acids.serine),
    tyrosine_g: parseNumeric(amino_acids.non_essential_amino_acids.tyrosine),
    l_aspartic_acid_g: parseNumeric(amino_acids.non_essential_amino_acids.l_aspartic_acid),
    l_glutamic_acid_g: parseNumeric(amino_acids.non_essential_amino_acids.l_glutamic_acid),
    l_glutamine_g: parseNumeric(amino_acids.non_essential_amino_acids.l_glutamine),

    // -- Amino acid derivatives -----------------------------------------------
    taurine_mg: parseNumeric(amino_acids.amino_acid_derivatives.taurine),
    carnitine_mg: parseNumeric(amino_acids.amino_acid_derivatives.carnitine),

    // -- Packaging ------------------------------------------------------------
    package_size_display: packaging.primary_package.unit_volume || undefined,
    net_volume_ml: parseNumeric(packaging.primary_package.unit_volume),
    volume_uom: packaging.primary_package.volume_uom || undefined,
    container_type: packaging.primary_package.unit_form || undefined,
    primary_unit_barcode: packaging.primary_package.unit_barcode || undefined,
    primary_unit_2d_barcode: packaging.primary_package.two_d_barcode || undefined,
    carton_barcode: packaging.carton_case.carton_barcode || undefined,
    case_barcode: packaging.carton_case.case_barcode || undefined,
    primary_units_per_carton: parseNumeric(packaging.carton_case.total_primary_units_in_carton),
    primary_units_per_case: parseNumeric(packaging.carton_case.total_primary_units_in_case),
    units_per_case: parseNumeric(packaging.carton_case.carton_quantity_within_case),
    // `null` (not `undefined`) so a cleared selection is sent explicitly and
    // actually clears the stored value — `undefined` is dropped by
    // JSON.stringify, so the API would otherwise just leave the previous
    // value in place instead of clearing it.
    storage_unopened: packaging.storage.storage_unopened || null,
    storage_opened: packaging.storage.storage_opened || null,
    shelf_life_after_open_days: parseNumeric(exp.expiration_after_opening),
    shelf_life_after_open_unit: exp.expiration_after_opening_unit || 'days',
    shelf_life_after_mix_refrig_hours: parseNumeric(exp.expiration_after_mixing),
    shelf_life_after_mix_refrig_unit: exp.expiration_after_mixing_unit || 'hours',

    // -- Other top-level fields -----------------------------------------------
    flavor: bi.flavor || undefined,
    age_for_use: joinCsv(bi.age_for_use),
    region_for_use: joinCsv(bi.region_for_use),
    lutein_mg: parseNumeric(other.other_compounds.lutein),
    mannitol_mg: parseNumeric(other.other_compounds.mannitol),
    sodium_metabisulfite_g: parseNumeric(other.other_compounds.sodium_metabisulfite),
  };
}

// ---------------------------------------------------------------------------
// Form payload → UpdateProductApiRequest (PATCH — flat body)
// ---------------------------------------------------------------------------

/**
 * Build a flat UpdateProductApiRequest from the multi-step form payload.
 * Used by the edit dialog. Same flat structure as POST.
 */
export function buildUpdateProductFromForm(
  payload: ProductRegistrationPayload,
): UpdateProductApiRequest {
  const bi = payload.overview.basic_information;
  const other = payload.other;

  // Start from the same flat body as create
  const body: UpdateProductApiRequest = buildCreateProductRequest(payload);

  // Override status with explicit value (edit allows all statuses)
  body.status = toApiStatus(bi.product_status);
  body.is_active = body.status === 'active';

  // Include needs_review for edits
  body.needs_review = parseBoolString(other.review_similar_products.needs_review) ?? undefined;

  return body;
}

/**
 * Build a minimal UpdateProductApiRequest from partial Product view-model fields.
 * Used by the inline edit flow in ProductDetailView.
 */
export function buildUpdateProductRequest(
  partial: Partial<Product>,
): UpdateProductApiRequest {
  const req: UpdateProductApiRequest = {};

  if (partial.sku !== undefined) req.sku = partial.sku;
  if (partial.productName !== undefined) req.product_name = partial.productName;
  if (partial.description !== undefined) req.description = partial.description;
  if (partial.brand !== undefined) req.brand_name = partial.brand;
  if (partial.form !== undefined) req.form_name = partial.form;
  if (partial.manufacturer !== undefined) req.manufacturer_name = partial.manufacturer;
  if (partial.ndc !== undefined) req.ndc_format_code = partial.ndc;
  if (partial.status !== undefined) req.status = viewStatusToApiStatus(partial.status);

  return req;
}

// ---------------------------------------------------------------------------
// API detail → Form payload (reverse transform for View / Edit)
// ---------------------------------------------------------------------------

/**
 * Convert a number | null | undefined attribute to the form string representation.
 * Returns empty string for null / undefined so form fields always have a string value.
 */
function numStr(val: number | null | undefined): string {
  return val != null ? String(val) : '';
}

/**
 * Convert a boolean | null | undefined attribute to "Yes" / "No" / "".
 */
function boolStr(val: boolean | null | undefined): string {
  if (val === true) return 'Yes';
  if (val === false) return 'No';
  return '';
}

/**
 * Convert a string | null | undefined attribute to a form string (empty string fallback).
 */
function str(val: string | null | undefined): string {
  return val ?? '';
}

/**
 * Split a comma-separated API string attribute into a multi-select form array.
 * These fields (age_for_use, region_for_use, protein_source, protein_hydrolysis,
 * cho_source) are stored as a single comma-joined string on the API/DB side —
 * only the form layer treats them as a multi-select array (SV-390).
 */
function splitCsv(val: string | null | undefined): string[] {
  return val ? val.split(',').map((s) => s.trim()).filter(Boolean) : [];
}

/**
 * Join a multi-select form array back into a comma-separated API string.
 * An empty array returns `null` (clear this field on PATCH) rather than
 * `undefined` (leave unchanged) — otherwise deselecting every chip could
 * never persist.
 */
function joinCsv(val: string[] | undefined): string | null | undefined {
  return val?.length ? val.join(', ') : null;
}

/**
 * Convert a boolean allergen flag from the API to a form boolean value.
 */
function allergenBoolToForm(val: boolean | null | undefined): boolean {
  return val === true;
}

/**
 * Transform an ApiProductDetail response into a ProductRegistrationPayload
 * that can be used to populate the multi-step product form for View / Edit flows.
 *
 * The API response is flat — all nutritional, allergen, and packaging fields
 * are at the top level of `detail`.
 */
export function apiDetailToFormPayload(
  detail: ApiProductDetail,
): ProductRegistrationPayload {
  return {
    // -- Step 1: Overview -------------------------------------------------------
    overview: {
      basic_information: {
        sku: str(detail.sku),
        tms_product_id: str(detail.tms_product_id),
        brand_name: str(detail.brand_name),
        manufacturer: str(detail.manufacturer_name),
        type_of_product: str(detail.type_of_product),
        form: str(detail.form_name),
        measurement_metric: '',
        calories_for_liquids: numStr(detail.kcal_per_ml),
        // When kcal_per_g is absent (e.g. product-request snapshots that only
        // store calories_kcal), fall back to calories_kcal — but only when it
        // differs from kcal_per_ml. Equal values mean calories_kcal was set as
        // the liquid fallback (buildCreateProductRequest: calories_kcal = pwds ?? liquids),
        // so showing it as the PWDS value would be incorrect.
        calories_for_pwds: numStr(
          detail.kcal_per_g ??
          (detail.calories_kcal !== detail.kcal_per_ml ? detail.calories_kcal : undefined)
        ),
        flavor: str(detail.flavor),
        product_status: str(detail.status),
        hcpcs: str(detail.hcpcs),
        ndc_format_code: str(detail.ndc_format_code),
        age_for_use: splitCsv(detail.age_for_use),
        region_for_use: splitCsv(detail.region_for_use),
        date_verified: str(detail.date_verified),
      },
      description: {
        product_description: str(detail.description),
        formula_classification: Array.isArray(detail.category_name) ? detail.category_name : (detail.category_name ? [detail.category_name] : []),
        parenteral_category: str(detail.parenteral_category),
        substitutions: str(detail.substitutions),
      },
      allergens: {
        egg: allergenBoolToForm(detail.contains_allergen_egg),
        fish: allergenBoolToForm(detail.contains_allergen_fish),
        lactose: allergenBoolToForm(detail.contains_allergen_lactose),
        latex: allergenBoolToForm(detail.contains_allergen_latex),
        milk: allergenBoolToForm(detail.contains_allergen_milk),
        peanut: allergenBoolToForm(detail.contains_allergen_peanut),
        sesame: allergenBoolToForm(detail.contains_allergen_sesame),
        shellfish: allergenBoolToForm(detail.contains_allergen_shellfish),
        soy: allergenBoolToForm(detail.contains_allergen_soy),
        tree_nuts: allergenBoolToForm(detail.contains_allergen_tree_nuts),
        wheat: allergenBoolToForm(detail.contains_allergen_wheat),
        contains_allergen_crustaceans: allergenBoolToForm(detail.contains_allergen_shellfish),
        contains_allergen_molluscs: allergenBoolToForm(detail.contains_allergen_molluscs),
        contains_allergen_celery: allergenBoolToForm(detail.contains_allergen_celery),
        contains_allergen_mustard: allergenBoolToForm(detail.contains_allergen_mustard),
        contains_allergen_sulphur_dioxide: allergenBoolToForm(detail.contains_allergen_sulphites),
        contains_allergen_sulphites: allergenBoolToForm(detail.contains_allergen_sulphites),
        contains_allergen_lupin: allergenBoolToForm(detail.contains_allergen_lupin),
      },
      verification: {
        verification_source_url: str(detail.verification_source),
      },
      expiration: {
        expiration_after_mixing: numStr(detail.shelf_life_after_mix_refrig_hours),
        expiration_after_mixing_unit: detail.shelf_life_after_mix_refrig_unit === 'days' ? 'days' : 'hours',
        expiration_after_opening: numStr(detail.shelf_life_after_open_days),
        expiration_after_opening_unit: detail.shelf_life_after_open_unit === 'hours' ? 'hours' : 'days',
      },
    },

    // -- Step 2: Nutritions -----------------------------------------------------
    nutritions: {
      caloric_information: {
        displacement_for_pwd: numStr(detail.displacement_ml_per_g),
        osmolality: numStr(detail.osmolality_mosm_per_kg),
        osmolarity: numStr(detail.osmolarity_mosm_per_l),
        prsl: numStr(detail.prsl_mosm_per_100kcal),
        renal_solute_load: numStr(detail.renal_solute_load_mosml),
        volume_to_provide_100_kcal: numStr(detail.ml_per_100kcal),
        water: numStr(detail.water_g),
      },
      protein: {
        protein: numStr(detail.protein_g),
        protein_source: splitCsv(detail.protein_source),
        protein_hydrolysis: splitCsv(detail.protein_hydrolysis),
        bcaa: '', // no API counterpart
        total_n2: numStr(detail.total_nitrogen_g),
      },
      fat: {
        fat: numStr(detail.total_fat_g),
        mct: numStr(detail.mct_g),
        ala: numStr(detail.ala_g),
        linoleic_acid_omega_6: numStr(detail.linoleic_omega6_mg),
        linolenic_acid_omega_3: numStr(detail.linolenic_omega3_mg),
        ara: numStr(detail.ara_mg),
        dha: numStr(detail.dha_mg),
        epa: numStr(detail.epa_mg),
        cholesterol: numStr(detail.cholesterol_mg),
      },
      carbohydrates: {
        cho: numStr(detail.cho_g),
        cho_source: splitCsv(detail.cho_source),
        added_sugars: numStr(detail.added_sugars_g),
        total_sugars: numStr(detail.total_sugars_g),
        dietary_fiber: numStr(detail.dietary_fiber_g),
        soluble_fiber: numStr(detail.soluble_fiber_g),
        insoluble_fiber: numStr(detail.insoluble_fiber_g),
        prebiotic_fiber: numStr(detail.prebiotic_fiber_g),
        dextrose: numStr(detail.dextrose_g),
        d_galactose: numStr(detail.d_galactose_g),
      },
      serving_information: {
        denominator_property: str(detail.nutrient_basis),
        denominator_volume: numStr(detail.net_volume_ml),
        recipe_dilution_for_reconstitution: str(detail.recipe_dilution),
        one_level_unpacked_cup: numStr(detail.cup_g),
        one_level_unpacked_teaspoon: numStr(detail.teaspoon_g),
        one_unpacked_level_scoop: numStr(detail.scoop_g),
      },
      ingredients: {
        full_ingredients_list: str(detail.ingredients),
      },
      dietary_information: {
        gluten_free: boolStr(detail.gluten_free),
        halal: boolStr(detail.halal),
        kosher: boolStr(detail.kosher),
      },
    },

    // -- Step 3: Vitamins -------------------------------------------------------
    vitamins: {
      b_vitamins: {
        biotin: numStr(detail.biotin_mcg),
        folate: numStr(detail.folate_dfe_mcg),
        folic_acid: numStr(detail.folate_mcg),
        niacin: numStr(detail.niacin_b3_mg),
        pantothenic_acid: numStr(detail.pantothenic_acid_mg),
        riboflavin: numStr(detail.riboflavin_b2_mg),
        thiamin: numStr(detail.thiamin_b1_mg),
        vitamin_b12: numStr(detail.vitamin_b12_mcg),
        vitamin_b6: numStr(detail.vitamin_b6_mg),
      },
      fat_soluble_vitamins: {
        vitamin_a_iu: '', // API only stores RAE; IU not returned
        vitamin_a_rae: numStr(detail.vitamin_a_mcg),
        vitamin_d_iu: '', // API only stores mcg; IU not returned
        vitamin_d_mcg: numStr(detail.vitamin_d_mcg),
        vitamin_e_iu: '', // API only stores mg; IU not returned
        vitamin_e_mg: numStr(detail.vitamin_e_mg),
        vitamin_k: numStr(detail.vitamin_k_mcg),
      },
      other_vitamins_related: {
        choline: numStr(detail.choline_mg),
        inositol: numStr(detail.inositol_mg),
        vitamin_c: numStr(detail.vitamin_c_mg),
      },
    },

    // -- Step 4: Minerals -------------------------------------------------------
    minerals: {
      electrolytes: {
        acetate: numStr(detail.acetate_meq),
        chloride_meq: numStr(detail.chloride_meq),
        chloride_mg: numStr(detail.chloride_mg),
        potassium_meq: numStr(detail.potassium_meq),
        potassium_mg: numStr(detail.potassium_mg),
        sodium_meq: numStr(detail.sodium_meq),
        sodium_mg: numStr(detail.sodium_mg),
      },
      major_minerals: {
        calcium_meq: numStr(detail.calcium_meq),
        calcium_mg: numStr(detail.calcium_mg),
        magnesium_meq: numStr(detail.magnesium_meq),
        magnesium_mg: numStr(detail.magnesium_mg),
        phosphorus_mmol: numStr(detail.phosphorus_mmol),
        phosphorus_mg: numStr(detail.phosphorus_mg),
      },
      trace_minerals: {
        aluminum: numStr(detail.aluminum_mcg),
        chromium: numStr(detail.chromium_mcg),
        copper: numStr(detail.copper_mg),
        fluoride: numStr(detail.fluoride_mcg),
        iodine: numStr(detail.iodine_mcg),
        iron: numStr(detail.iron_mg),
        manganese: numStr(detail.manganese_mg),
        molybdenum: numStr(detail.molybdenum_mcg),
        selenium: numStr(detail.selenium_mcg),
        zinc: numStr(detail.zinc_mg),
      },
    },

    // -- Step 5: Amino Acids ----------------------------------------------------
    amino_acids: {
      essential_amino_acids: {
        histidine: numStr(detail.histidine_g),
        isoleucine: numStr(detail.isoleucine_g),
        leucine: numStr(detail.leucine_g),
        lysine: numStr(detail.lysine_g),
        methionine: numStr(detail.methionine_g),
        phenylalanine: numStr(detail.phenylalanine_g),
        threonine: numStr(detail.threonine_g),
        tryptophan: numStr(detail.tryptophan_g),
        valine: numStr(detail.valine_g),
      },
      non_essential_amino_acids: {
        alanine: numStr(detail.alanine_g),
        arginine: numStr(detail.arginine_g),
        cystine: numStr(detail.cystine_g),
        glycine: numStr(detail.glycine_g),
        l_aspartic_acid: numStr(detail.l_aspartic_acid_g),
        l_glutamic_acid: numStr(detail.l_glutamic_acid_g),
        l_glutamine: numStr(detail.l_glutamine_g),
        proline: numStr(detail.proline_g),
        serine: numStr(detail.serine_g),
        tyrosine: numStr(detail.tyrosine_g),
      },
      amino_acid_derivatives: {
        taurine: numStr(detail.taurine_mg),
        carnitine: numStr(detail.carnitine_mg),
      },
    },

    // -- Step 6: Packaging ------------------------------------------------------
    packaging: {
      primary_package: {
        two_d_barcode: str(detail.primary_unit_2d_barcode),
        unit_barcode: str(detail.primary_unit_barcode),
        unit_form: str(detail.container_type),
        // net_volume_ml is the canonical numeric volume (ml); package_size_display is a
        // free-text label that product-request snapshots often omit.
        unit_volume: numStr(detail.net_volume_ml) || str(detail.package_size_display),
        volume_uom: str(detail.volume_uom),
      },
      carton_case: {
        carton_barcode: str(detail.carton_barcode),
        carton_quantity_within_case: numStr(detail.units_per_case),
        case_barcode: str(detail.case_barcode),
        total_primary_units_in_carton: numStr(detail.primary_units_per_carton),
        total_primary_units_in_case: numStr(detail.primary_units_per_case),
      },
      storage: {
        storage_opened: str(detail.storage_opened),
        storage_unopened: str(detail.storage_unopened),
      },
    },

    // -- Step 7: Other ----------------------------------------------------------
    other: {
      identifiers: {
        over_arching_number_nutrition_source: str(detail.overarching_number_ns),
        over_arching_number_pens: str(detail.overarching_number_pens),
        over_arching_number_tmnp: str(detail.overarching_number_tmnp),
      },
      other_compounds: {
        lutein: numStr(detail.lutein_mg),
        mannitol: numStr(detail.mannitol_mg),
        sodium_metabisulfite: numStr(detail.sodium_metabisulfite_g),
      },
      review_similar_products: {
        needs_review: boolStr(detail.needs_review ?? null),
        similar_products: detail.similar_products?.join(', ') ?? '',
        similarity_details: str(detail.similarity_details),
      },
    },
  };
}
